import { isDefined } from '@sgme/fp';
import { Button } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { useCallback, useMemo } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';

import { type AccumulatorType, getTradeExamples } from '@/App/tiles/shared/import/common.ts';
import { FileImportInput } from '@/App/tiles/shared/import/FileImportInput';
import { TRADE_TYPE_TO_EXAMPLE, type TradeTypeExample } from '@/App/tiles/shared/import/typings.ts';
import { CacheProofLink } from '@/components/ui/CacheProofLink.tsx';

interface ImportModalProps {
  show: boolean;
  onClose: () => void;
  onImport: (file: File) => void;
  tradeType: TradeTypeExample;
  accuType?: AccumulatorType;
  file: File | undefined;
  setFile: (file: File | undefined) => void;
}

export function ImportModal({ show, onClose, onImport, tradeType, accuType, file, setFile }: ImportModalProps) {
  function handleConfirm() {
    if (isDefined(file)) {
      onImport(file);
    }
  }

  return (
    <Modal show={show} onHide={onClose}>
      <ModalHeader>
        <FormattedMessage tagName="h4" id="modal.import.title" />
      </ModalHeader>

      <ModalBody>
        <div className="mb-4">
          <Instructions tradeType={tradeType} accuType={accuType} />
        </div>
        <FileImportInput file={file} onFileChange={setFile} />
      </ModalBody>

      <ModalFooter>
        <Button data-e2e="cancel-button" purpose="outline" color="primary" onClick={onClose}>
          <FormattedMessage id="modal.import.cancel" />
        </Button>
        <Button onClick={handleConfirm} data-e2e="import-button" disabled={file === null}>
          <FormattedMessage id="modal.import.title" />
        </Button>
      </ModalFooter>
    </Modal>
  );
}

interface InstructionsProps {
  tradeType: TradeTypeExample;
  accuType?: AccumulatorType;
}

function Instructions({ tradeType, accuType }: InstructionsProps) {
  const { sgwtHelpCenter } = useSgwtWidgets();

  const helpCenterTopic = 'modal.import';
  const openHelp = useCallback(() => {
    return sgwtHelpCenter?.topic(helpCenterTopic);
  }, [sgwtHelpCenter]);

  const tradeTypeExamples = getTradeExamples(tradeType, accuType);

  const prefix = TRADE_TYPE_TO_EXAMPLE[tradeTypeExamples];

  const values = useMemo(
    () => ({
      help: (
        // oxlint-disable-next-line anchor-is-valid -- opens the help center widget, not a real navigation link
        <a href="#" onClick={openHelp}>
          <FormattedMessage id="modal.import.help" tagName="u" />
        </a>
      ),

      link: (
        <CacheProofLink href={`/samples/${prefix}Example.csv`} fileName={`SGME-FX-${prefix}.csv`}>
          <FormattedMessage id="modal.import.link" tagName="u" />
        </CacheProofLink>
      ),
    }),
    [openHelp, prefix],
  );
  const messageId = tradeType === 'Option' ? 'modal.import.option.message' : 'modal.import.message';
  return (
    <div className="mb-4">
      <FormattedMessage id={messageId} values={values} />
    </div>
  );
}
